import type { ReactNode } from 'react';
import { ModelPicker } from '@/components/ModelPicker';
import { VoicePicker } from '@/components/VoicePicker';
import { KNOWN_PAYLOAD_TYPES } from '@/protocol/payloads';
import { deployedEndpoint, ENDPOINT_PRESETS } from '@/session/config';
import { modelLabel } from '@/session/models';
import { ttsSummary } from '@/session/tts';
import { useSession } from '@/session/store';

/** The endpoint, the model, the handshake fields, and a readout of what the handshake produced. */
export function SettingsPanel() {
  const endpoint = useSession((state) => state.endpoint);
  const setEndpoint = useSession((state) => state.setEndpoint);
  const resetEndpoint = useSession((state) => state.resetEndpoint);
  const signIn = useSession((state) => state.signIn);
  const patchSignIn = useSession((state) => state.patchSignIn);
  const voiceMode = useSession((state) => state.voiceMode);
  const setVoiceMode = useSession((state) => state.setVoiceMode);
  const dropAbandonedText = useSession((state) => state.dropAbandonedText);
  const setDropAbandonedText = useSession((state) => state.setDropAbandonedText);
  const connection = useSession((state) => state.connection);
  const sessionId = useSession((state) => state.sessionId);
  const deviceMode = useSession((state) => state.deviceMode);
  const modelId = useSession((state) => state.modelId);
  const sessionModelId = useSession((state) => state.sessionModelId);
  const ttsProvider = useSession((state) => state.ttsProvider);
  const ttsModel = useSession((state) => state.ttsModel);
  const ttsVoice = useSession((state) => state.ttsVoice);
  const ttsCatalog = useSession((state) => state.ttsCatalog);
  const sessionTts = useSession((state) => state.sessionTts);
  const reconnect = useSession((state) => state.reconnect);
  const connect = useSession((state) => state.connect);

  const deployed = deployedEndpoint();
  // The open session cannot change model, so a selection it was not signed in with is a pending one.
  const pendingModel = sessionModelId !== null && sessionModelId !== modelId;
  // Likewise the voice: a selection the open session was not signed in with takes a reconnect.
  const selectedTts = ttsSummary(ttsCatalog, ttsProvider, ttsModel, ttsVoice);
  const pendingTts = sessionTts !== null && sessionTts !== selectedTts;

  return (
    <>
      <section className="dbg-group">
        <h3 className="dbg-group-title">Endpoint</h3>

        <div className="mb-1.5 flex gap-1.5">
          {ENDPOINT_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              title={preset.note}
              aria-pressed={endpoint === preset.url}
              onClick={() => setEndpoint(preset.url)}
              className="dbg-preset flex-1"
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="dbg-fields">
          <Text label="ws url" value={endpoint} onChange={setEndpoint} onSubmit={reconnect} wide />
        </div>

        {endpoint.trim() !== deployed && (
          <button type="button" onClick={resetEndpoint} className="dbg-note mt-1.5 block text-left">
            ← back to the deployed default ({deployed})
          </button>
        )}
      </section>

      <section className="dbg-group">
        <h3 className="dbg-group-title">Model</h3>
        <div className="dbg-fields">
          <Labelled label="provider · model" wide>
            <ModelPicker className="dbg-input" />
          </Labelled>
        </div>
        <p className="dbg-note mt-1.5">
          {pendingModel
            ? `This session is on ${modelLabel(sessionModelId)} — reconnect to sign in on ${modelLabel(modelId)}.`
            : 'Sent in the handshake as the provider and model id, or left out entirely on the Andromeda default. A provider the deployment has disabled is refused at sign-in.'}
        </p>
      </section>

      <section className="dbg-group">
        <h3 className="dbg-group-title">Voice</h3>
        <div className="dbg-fields">
          <Labelled label="provider · voice" wide>
            <VoicePicker className="dbg-input" />
          </Labelled>
        </div>
        <p className="dbg-note mt-1.5">
          {pendingTts
            ? `This session speaks with ${sessionTts} — reconnect to sign in with ${selectedTts}.`
            : 'Sent in the handshake as the provider, model and voice, or left out entirely on the Andromeda default. A provider the deployment has disabled is refused at sign-in.'}
        </p>
      </section>

      <section className="dbg-group">
        <h3 className="dbg-group-title">Receiver</h3>
        <div className="dbg-fields">
          <Readout label="device mode" value={deviceMode ?? '—'} />
          <label className="dbg-field">
            <span>voice mode</span>
            <select
              value={voiceMode}
              onChange={(event) => setVoiceMode(event.target.value as typeof voiceMode)}
              disabled={connection === 'open'}
              title={
                connection === 'open'
                  ? 'Driven by the server once a session is open — Andromeda can redirect it with changeVoiceState.'
                  : undefined
              }
              className="dbg-input"
            >
              <option value="compact">compact</option>
              <option value="fullScreen">fullScreen</option>
            </select>
          </label>
          <Text
            label="household id"
            value={signIn.householdId}
            onChange={(value) => patchSignIn('householdId', value)}
            onSubmit={reconnect}
          />
          <Text
            label="receiver id"
            value={signIn.receiverId}
            onChange={(value) => patchSignIn('receiverId', value)}
            onSubmit={reconnect}
          />
          <Text
            label="user id"
            value={signIn.userId ?? ''}
            onChange={(value) => patchSignIn('userId', value)}
            onSubmit={reconnect}
          />
          <Text
            label="entitlement id"
            value={signIn.entitlementId ?? ''}
            onChange={(value) => patchSignIn('entitlementId', value)}
            onSubmit={reconnect}
          />
          <Text
            label="zip"
            value={signIn.zipcode}
            onChange={(value) => patchSignIn('zipcode', value)}
            onSubmit={reconnect}
          />
          <Text
            label="dma"
            value={signIn.dma}
            onChange={(value) => patchSignIn('dma', value)}
            onSubmit={reconnect}
          />
        </div>
      </section>

      {/* None of these can change on a live socket: the URL is fixed when the socket is constructed
          and the rest is only ever sent in the sign-in handshake. */}
      <div className="dbg-apply">
        <button
          type="button"
          onClick={connection === 'idle' ? () => connect(deviceMode ?? 'DISH') : reconnect}
          disabled={connection === 'connecting'}
          className="dbg-connect"
        >
          {connection === 'connecting'
            ? 'Connecting'
            : connection === 'idle'
              ? 'Connect'
              : 'Reconnect'}
        </button>
        <p className="dbg-note">
          Signs in again on a new session, which clears the conversation along with the turns and
          the frame log — the server issues a new session id and no longer remembers the old one.
        </p>
      </div>

      <section className="dbg-group">
        <h3 className="dbg-group-title">Barge-in</h3>
        <label
          className="dbg-switch"
          title="On, a talked-over reply's remaining text is dropped. Off, it is rendered as it arrives and its end marker ends the new reply early — what a client that only stops its audio will do. Speech is silenced either way."
        >
          <input
            type="checkbox"
            role="switch"
            checked={dropAbandonedText}
            onChange={(event) => setDropAbandonedText(event.target.checked)}
          />
          <span>drop abandoned text</span>
        </label>
        <p className="dbg-note">
          Takes effect on the next interruption; nothing to reconnect for. The server abandons the
          reply either way — this is only what this client does with the frames already in flight.
        </p>
      </section>

      <section className="dbg-group">
        <h3 className="dbg-group-title">Session</h3>
        <div className="dbg-fields">
          <Readout label="session id" value={sessionId ?? '—'} wide />
          <Readout
            label="socket"
            value={
              connection === 'open' ? 'open' : connection === 'connecting' ? 'signing in' : 'closed'
            }
          />
          <Readout
            label="signed in on"
            value={sessionModelId === null ? '—' : modelLabel(sessionModelId)}
          />
          <Readout label="speaks with" value={sessionTts ?? '—'} />
          <Readout label="renders" value={`${KNOWN_PAYLOAD_TYPES.length} payload types`} wide />
        </div>
      </section>
    </>
  );
}

function Text({
  label,
  value,
  onChange,
  onSubmit,
  wide,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  wide?: boolean;
}) {
  return (
    <Labelled label={label} wide={wide}>
      <input
        value={value}
        spellCheck={false}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onSubmit();
        }}
        className="dbg-input"
      />
    </Labelled>
  );
}

function Readout({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <Labelled label={label} wide={wide}>
      <output className="dbg-output">{value}</output>
    </Labelled>
  );
}

function Labelled({
  label,
  wide,
  children,
}: {
  label: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={wide ? 'dbg-field dbg-field-wide' : 'dbg-field'}>
      <span>{label}</span>
      {children}
    </label>
  );
}
